import createSculptBrushStore from './createSculptBrushStore';
import { BRUSH_LIMITS, BRUSH_MODE } from '../lib/sculpt/brushSettings';
import { DEFAULT_BRUSH_FALLOFF } from '../lib/sculpt/brushFalloff';

/**
 * The Inflate / Deflate brush's settings, as the card shows them: millimetres
 * and percentages, converted to kernel units by `toBrushSettings`.
 */
export const INFLATE_BRUSH_DEFAULTS = {
  mode: BRUSH_MODE.INFLATE,
  /** Brush radius in millimetres. */
  size: BRUSH_LIMITS.size.default,
  /** 1..100; how fast a stroke reaches its displacement limit. */
  intensity: BRUSH_LIMITS.intensity.default,
  /** Millimetres a single stroke may move the surface. */
  maxDisplacement: BRUSH_LIMITS.maxDisplacement.default,
  /** 0..100; relax applied alongside each dab, to stop inflate spiking. */
  smoothing: BRUSH_LIMITS.smoothing.default,
  /**
   * Fixed at the smooth curve — the card no longer offers a picker. The
   * other curves stay available to `getFalloffFn` for any brush that wants
   * them; on these two, sphere/sharp/linear only ever produced a harder
   * edge than socket work wants.
   */
  falloff: DEFAULT_BRUSH_FALLOFF,
  /** Leave open-boundary (trimline rim) vertices pinned. */
  pinBoundary: true,
  /**
   * Hold the enclosed volume constant, so a stroke redistributes material
   * instead of adding it. On by default so a local push does not grow or
   * shrink the socket.
   */
  lockVolume: true,
};

/**
 * Factory for a per-step Inflate / Deflate store.
 *
 * All the machinery is in `createSculptBrushStore`, which every sculpt brush
 * shares; what is left here is this brush's vocabulary — the settings its card
 * shows and its kernels read.
 *
 * @param {Object} options
 * @param {string} options.name - devtools store name; must be unique per instance
 * @param {Partial<typeof INFLATE_BRUSH_DEFAULTS>} [options.brushDefaults]
 *   - per-step overrides, e.g. a larger default brush on a step that works at
 *     coarser scale
 * @returns {import('zustand').UseBoundStore<any>}
 */
export function createInflateDeflateStore({ name, brushDefaults = {} }) {
  return createSculptBrushStore({
    name,
    appliedMeshName: 'InflateDeflateApplied',
    brushDefaults: { ...INFLATE_BRUSH_DEFAULTS, ...brushDefaults },
  });
}

export default createInflateDeflateStore;
